"use client"

import { HttpTypes } from "@medusajs/types"
import Image from "next/image"
import { useCallback, useEffect, useMemo, useState } from "react"
import { toDisplayImageUrl } from "@lib/util/encode-remote-image-url"

type ImageGalleryProps = {
  images: HttpTypes.StoreProductImage[]
  title: string
}

const ImageGallery = ({ images, title }: ImageGalleryProps) => {
  const galleryImages = useMemo(
    () =>
      images
        .filter((image) => Boolean(image.url))
        .map((image) => ({
          ...image,
          url: toDisplayImageUrl(image.url),
        })),
    [images]
  )
  const [activeIndex, setActiveIndex] = useState(0)

  const activeImage = galleryImages[activeIndex] || galleryImages[0]

  const showPrevious = useCallback(() => {
    if (!galleryImages.length) return
    setActiveIndex((current) =>
      current === 0 ? galleryImages.length - 1 : current - 1
    )
  }, [galleryImages.length])

  const showNext = useCallback(() => {
    if (!galleryImages.length) return
    setActiveIndex((current) =>
      current === galleryImages.length - 1 ? 0 : current + 1
    )
  }, [galleryImages.length])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") showPrevious()
      if (event.key === "ArrowRight") showNext()
    }

    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [showNext, showPrevious])

  if (!activeImage?.url) {
    return (
      <div className="pdp-gallery-empty flex aspect-square items-center justify-center bg-white text-sm text-mt-muted">
        ไม่มีรูปภาพสินค้า
      </div>
    )
  }

  return (
    <div className="pdp-gallery" data-testid="product-gallery">
      <div className="pdp-gallery-main mikrotik-product-media relative aspect-square bg-white">
        <Image
          src={activeImage.url}
          alt={title}
          fill
          priority
          sizes="(max-width: 1024px) 100vw, 50vw"
          className="object-contain p-6"
          referrerPolicy="no-referrer"
        />
        {galleryImages.length > 1 && (
          <div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-between px-2">
            <button
              type="button"
              onClick={showPrevious}
              className="pdp-gallery-nav"
              aria-label="รูปก่อนหน้า"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={showNext}
              className="pdp-gallery-nav"
              aria-label="รูปถัดไป"
            >
              ›
            </button>
          </div>
        )}
      </div>

      {galleryImages.length > 1 && (
        <div className="mt-4 flex gap-3 overflow-x-auto pb-1 no-scrollbar">
          {galleryImages.map((image, index) => (
            <button
              key={image.id || `${image.url}-${index}`}
              type="button"
              onClick={() => setActiveIndex(index)}
              className={`pdp-gallery-thumb ${
                index === activeIndex ? "pdp-gallery-thumb-active" : ""
              }`}
              aria-label={`แสดงรูปที่ ${index + 1}`}
            >
              {image.url && (
                <Image
                  src={image.url}
                  alt={`${title} thumbnail ${index + 1}`}
                  fill
                  sizes="72px"
                  className="object-contain p-2"
                  referrerPolicy="no-referrer"
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default ImageGallery
